/**
 * TASK-TGE-002 & TASK-TGE-003: Glowing Line Renderer with Pulse Animation
 *
 * Renders neon-style glowing lines using multi-pass rendering technique.
 * Includes time-based pulse animation for breathing glow effect.
 */

import * as PIXI from 'pixi.js';
import type { GridEffectsConfig } from '../../types/terrain';

/**
 * Draw a glowing edge with multi-pass glow effect
 * Creates a neon-style glow by layering multiple lines with decreasing opacity
 *
 * @param graphics - PIXI.Graphics instance to draw on
 * @param start - Start point of the line
 * @param end - End point of the line
 * @param config - Grid effects configuration
 * @param pulseIntensity - Current pulse intensity multiplier (0-1)
 */
export function drawGlowingEdge(
  graphics: PIXI.Graphics,
  start: { x: number; y: number },
  end: { x: number; y: number },
  config: GridEffectsConfig,
  pulseIntensity: number = 1.0
): void {
  const { glowColor, glowIntensity } = config;
  const effectiveIntensity = glowIntensity * pulseIntensity;

  // Multi-pass glow: Outer blur effect (4 passes with increasing width and decreasing alpha)
  for (let i = 3; i >= 0; i--) {
    const alpha = (effectiveIntensity * 0.2) / (i + 1);
    const width = 2 + i * 2; // Width: 8, 6, 4, 2

    graphics.moveTo(start.x, start.y);
    graphics.lineTo(end.x, end.y);
    graphics.stroke({
      color: glowColor,
      alpha,
      width,
      cap: 'round',
    });
  }

  // Core line: Bright center with white color
  graphics.moveTo(start.x, start.y);
  graphics.lineTo(end.x, end.y);
  graphics.stroke({
    color: 0xffffff,
    alpha: effectiveIntensity * 0.8,
    width: 2,
    cap: 'round',
  });
}

/**
 * Calculate the current glow pulse intensity based on time
 * Creates a breathing animation effect using sine wave oscillation
 *
 * @param time - Current time in seconds
 * @param config - Grid effects configuration
 * @returns Intensity multiplier (oscillates between 1-glowPulseAmount and 1+glowPulseAmount)
 */
export function calculateGlowPulse(time: number, config: GridEffectsConfig): number {
  const { glowPulseSpeed, glowPulseAmount } = config;

  // Sine wave oscillation centered at 1.0
  const oscillation = Math.sin(time * glowPulseSpeed * Math.PI * 2);
  const intensity = 1.0 + oscillation * glowPulseAmount;

  return intensity;
}
